import { message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import {
  applyOepDocument,
  deleteOepDocument,
  getContributionFileDownloadUrl,
  getErrorFileDownloadUrl,
  getOepDocumentDownloadUrl,
  retryOepJob,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'
import { resetOpenEnrollmentCompanyQueries } from 'modules/annual-benefit-planning/modules/shared/infra/react-query/reset-open-enrollment-company-queries'
import {
  extractErrorMessage,
  extractErrorStatus,
} from 'routes/AbpUploadDashboard/utils/extract-error-message'

export const RETRY_FAILED_MESSAGE = 'Retry failed. Please try again.'

// 400 (no longer has errors) and 409 (already running) carry a message worth
// showing, and mean the row's state changed — so the list refreshes.
const RETRY_STALE_STATUSES: ReadonlySet<number> = new Set([400, 409])

interface Options {
  readonly clientId: string
}

interface Result {
  // The documentId currently mid-action ('delete' | 'apply' | 'retry' |
  // 'download' | 'download-contribution' | 'download-error'), or null when idle — a row's
  // action controls disable off this while a request for that row is in flight.
  readonly busy: {
    readonly documentId: string
    readonly action:
      | 'delete'
      | 'apply'
      | 'retry'
      | 'download'
      | 'download-contribution'
      | 'download-error'
  } | null
  readonly handleDelete: (documentId: string) => Promise<void>
  readonly handleApply: (documentId: string) => Promise<void>
  readonly handleRetry: (
    documentId: string,
    analysisJobId: string
  ) => Promise<void>
  readonly handleDownload: (documentId: string) => Promise<void>
  readonly handleDownloadContribution: (documentId: string) => Promise<void>
  readonly handleDownloadError: (documentId: string) => Promise<void>
}

/**
 * Per-row Delete/Apply/Retry/Download/DownloadContribution actions for the
 * Analysis Files table (EMS-10154 FR-013, Apply restored by EMS-11022).
 * Delete and Apply mutate a single document rather than the whole list, so
 * on success they invalidate the
 * table's own list query plus the client-scoped approval document cache
 * (mirrors the invalidation guarantee `useOepDocumentUpload` already
 * provides for uploads). Downloads are read-only — each opens a
 * presigned S3 URL in a new tab (same pattern as
 * ApprovalSummaryCard/OpenEnrollmentApprovalContainer) and invalidates
 * nothing.
 */
export function useOepDocumentRowActions({ clientId }: Options): Result {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<Result['busy']>(null)
  // `busy` only disables Apply after a re-render, so a fast double-click
  // would send two requests; this blocks the second one at once (EMS-11070).
  const isApplyingRef = useRef(false)
  // Same double-click guard for Retry (EMS-11146).
  const isRetryingRef = useRef(false)

  const invalidateAfterMutation = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
        }),
        queryClient.invalidateQueries({
          queryKey: APPROVAL_QUERY_KEYS.oepDocumentByClient(clientId),
        }),
      ]),
    [queryClient, clientId]
  )

  const handleDelete = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'delete' })
      try {
        await deleteOepDocument(clientId, documentId)
        void invalidateAfterMutation()
      } catch (error) {
        message.error('Delete failed. Please try again.')
        throw error
      } finally {
        setBusy(null)
      }
    },
    [clientId, invalidateAfterMutation]
  )

  // Makes the file the current one for its plan year. The approval document
  // cache is invalidated too, so the Approval tab shows it without a refresh.
  const handleApply = useCallback(
    async (documentId: string) => {
      if (isApplyingRef.current) return
      isApplyingRef.current = true
      setBusy({ documentId, action: 'apply' })
      try {
        await applyOepDocument(clientId, documentId)
      } catch {
        message.error('Apply failed. Please try again.')
        isApplyingRef.current = false
        setBusy(null)
        return
      }
      // Stay busy until the list shows the new current file, so Apply can't
      // be clicked again against the old status. The ABP 5 jobs list carries
      // the generated files' current flag (EMS-11070).
      // EMS-11156 (D8): the applied file changes what the Open Enrollment page
      // shows, so drop the client's cached page data (People and
      // Contributions excepted) — the next visit loads it fresh.
      await Promise.allSettled([
        resetOpenEnrollmentCompanyQueries(queryClient, clientId),
        invalidateAfterMutation(),
        queryClient.invalidateQueries({
          queryKey: APPROVAL_QUERY_KEYS.abp5FileJobsByDocument(clientId),
        }),
      ])
      message.success('Applied.')
      isApplyingRef.current = false
      setBusy(null)
    },
    [clientId, invalidateAfterMutation, queryClient]
  )

  // Re-runs a "generated with errors" row's analysis job (EMS-11146). No
  // success toast: the refreshed row shows "Processing…" and its poll takes
  // it to the new result.
  const handleRetry = useCallback(
    async (documentId: string, analysisJobId: string) => {
      if (isRetryingRef.current) return
      isRetryingRef.current = true
      setBusy({ documentId, action: 'retry' })
      try {
        await retryOepJob(analysisJobId)
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
          }),
          queryClient.invalidateQueries({
            queryKey: APPROVAL_QUERY_KEYS.oepDocumentProcessingStatus(
              clientId,
              documentId
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: APPROVAL_QUERY_KEYS.abp5FileJobsByDocument(clientId),
          }),
        ])
      } catch (error) {
        const status = extractErrorStatus(error)
        if (status !== undefined && RETRY_STALE_STATUSES.has(status)) {
          message.error(extractErrorMessage(error, RETRY_FAILED_MESSAGE))
          await queryClient
            .invalidateQueries({
              queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
            })
            .catch(() => undefined)
        } else {
          message.error(RETRY_FAILED_MESSAGE)
        }
      } finally {
        isRetryingRef.current = false
        setBusy(null)
      }
    },
    [clientId, queryClient]
  )

  const handleDownload = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'download' })
      try {
        const { presignedUrl } = await getOepDocumentDownloadUrl(
          clientId,
          documentId
        )
        // The presigned URL carries a Content-Disposition: attachment header
        // (set server-side), so opening it downloads the file directly.
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch {
        message.error('Download failed. Please try again.')
      } finally {
        setBusy(null)
      }
    },
    [clientId]
  )

  const handleDownloadContribution = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'download-contribution' })
      try {
        const { presignedUrl } = await getContributionFileDownloadUrl(
          clientId,
          documentId
        )
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch {
        message.error('Download failed. Please try again.')
      } finally {
        setBusy(null)
      }
    },
    [clientId]
  )

  const handleDownloadError = useCallback(
    async (documentId: string) => {
      setBusy({ documentId, action: 'download-error' })
      try {
        const { presignedUrl } = await getErrorFileDownloadUrl(
          clientId,
          documentId
        )
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch {
        message.error('Download failed. Please try again.')
      } finally {
        setBusy(null)
      }
    },
    [clientId]
  )

  return {
    busy,
    handleDelete,
    handleApply,
    handleRetry,
    handleDownload,
    handleDownloadContribution,
    handleDownloadError,
  }
}
